/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Envelope, ArrowBendUpLeft, CheckCircle, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

// INDIGO-INBOX theme — distinct from Demographics/Revenue/Ads/Receipt.
const TH = {
  bg:        "#0B0A14",
  surface:   "#13121F",
  surfaceHi: "#1D1B30",
  border:    "#28253E",
  borderHi:  "#3C3859",
  accent:    "#A78BFA",      // amethyst
  accentDim: "#5B4193",
  warm:      "#FFD17B",
  pop:       "#FCA5A5",
  green:     "#5CD79B",
  text:      "#F0EAFF",
  textMute:  "#9890B5",
  textDim:   "#665E85",
};

const FILTERS = [
  { key: "",        label: "All" },
  { key: "new",     label: "Unread" },
  { key: "read",    label: "Read" },
  { key: "replied", label: "Replied" },
];

const STATUS_BADGE = {
  new:     { label: "New",      color: "#FCA5A5" },
  read:    { label: "Read",     color: "#A78BFA" },
  replied: { label: "Replied",  color: "#5CD79B" },
};

export default function AdminContactQueriesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [filter, setFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});  // queryId → boolean

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Contact Queries is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const refresh = () => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    const params = { limit: 100 };
    if (filter) params.status = filter;
    api.get("/super-admin/contact-queries", { params })
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load queries."))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [user, filter]); // eslint-disable-line

  const mark = async (queryId, status) => {
    setBusy((b) => ({ ...b, [queryId]: true }));
    try {
      await api.post(`/super-admin/contact-queries/${queryId}/mark`, { status });
      setData((d) => ({
        ...d,
        queries: d.queries.map((q) =>
          q.query_id === queryId
            ? {
                ...q,
                status,
                ...(status === "read"    && { read_at: new Date().toISOString() }),
                ...(status === "replied" && { replied_at: new Date().toISOString() }),
              }
            : q,
        ),
        unread: status !== "new"
          ? Math.max(0, (d.unread || 0) - (d.queries.find((q) => q.query_id === queryId)?.status === "new" ? 1 : 0))
          : d.unread,
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status.");
    } finally {
      setBusy((b) => ({ ...b, [queryId]: false }));
    }
  };

  const reply = (q) => {
    // Mark as replied + open Gmail compose.
    mark(q.query_id, "replied");
    const subject = encodeURIComponent(`Re: ${q.subject || "Your AstroKuber query"}`);
    const body = encodeURIComponent(
      `Hi ${q.name.split(" ")[0]},\n\nThanks for reaching out to AstroKuber.\n\n— Team AstroKuber\n\n---\nOn ${new Date(q.created_at).toLocaleString("en-IN")}, you wrote:\n${q.message.split("\n").map((l) => "> " + l).join("\n")}`,
    );
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(q.email)}&su=${subject}&body=${body}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const rows = data?.queries || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: TH.bg, color: TH.text }}>
      <Header />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-14">
        <button type="button" onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] mb-7 hover:opacity-100"
          style={{ color: TH.textMute, opacity: 0.7 }}
          data-testid="contact-admin-back">
          <ArrowLeft size={12} weight="bold" /> Back
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] mb-5"
          style={{ backgroundColor: `${TH.accent}1A`, color: TH.accent, border: `1px solid ${TH.accentDim}` }}>
          <Envelope size={11} weight="bold" /> Super-Admin · Contact Queries
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
          Visitor <span className="italic" style={{ color: TH.accent }}>queries</span>
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed font-light" style={{ color: TH.textMute }}>
          Every message sent through the public Contact form lands here, with a copy forwarded
          to <b style={{ color: TH.text }}>astrologykuber@gmail.com</b>. Reply with one click —
          we'll pre-fill your Gmail compose for you and mark the query as Replied.
        </p>

        {/* Filter chips */}
        <div className="mt-7 flex flex-wrap items-center gap-2" data-testid="contact-admin-filters">
          {FILTERS.map((f) => (
            <button key={f.key || "all"} onClick={() => setFilter(f.key)}
              className="px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider transition-all"
              style={{
                backgroundColor: filter === f.key ? TH.accent : "transparent",
                color: filter === f.key ? TH.bg : TH.textMute,
                border: `1px solid ${filter === f.key ? TH.accent : TH.border}`,
                fontWeight: filter === f.key ? 700 : 400,
              }}
              data-testid={`contact-admin-filter-${f.key || "all"}`}>
              {f.label}
              {f.key === "new" && data?.unread > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold"
                  style={{ background: TH.pop, color: "#7F1D1D" }}>
                  {data.unread}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-10 text-center font-mono text-sm" style={{ color: TH.textDim }}>
            Loading queries…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="mt-10 rounded-xl p-8 text-center" style={{ background: TH.surface, border: `1px solid ${TH.border}`, color: TH.textMute }}>
            No queries here.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <ul className="mt-7 space-y-3" data-testid="contact-admin-list">
            {rows.map((q) => {
              const sb = STATUS_BADGE[q.status] || STATUS_BADGE.new;
              const date = new Date(q.created_at);
              return (
                <li key={q.query_id} className="rounded-xl p-5"
                  style={{ background: TH.surface, border: `1px solid ${TH.border}` }}
                  data-testid={`contact-admin-row-${q.query_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-medium" style={{ color: TH.text }}>{q.name}</span>
                        <span className="font-mono text-[11px]" style={{ color: TH.warm }}>{q.email}</span>
                        <span className="font-mono text-[10px]" style={{ color: TH.textDim }}>· {q.country || "—"}</span>
                      </div>
                      {q.subject && (
                        <div className="mt-1 text-sm" style={{ color: TH.text }}>
                          <b>Subject:</b> {q.subject}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-2 py-0.5 rounded-full font-mono text-[10px]"
                        style={{ background: `${sb.color}22`, color: sb.color, border: `1px solid ${sb.color}55` }}>
                        {sb.label}
                      </span>
                      <span className="font-mono text-[10px]" style={{ color: TH.textDim }}>
                        {date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg p-3 font-light text-sm leading-relaxed whitespace-pre-wrap"
                    style={{ background: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}` }}>
                    {q.message}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button onClick={() => reply(q)}
                      disabled={!!busy[q.query_id]}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all disabled:opacity-50"
                      style={{ background: TH.accent, color: TH.bg }}
                      data-testid={`contact-admin-reply-${q.query_id}`}>
                      <ArrowBendUpLeft size={12} weight="bold" /> Reply via Gmail
                    </button>
                    {q.status === "new" && (
                      <button onClick={() => mark(q.query_id, "read")}
                        disabled={!!busy[q.query_id]}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all disabled:opacity-50"
                        style={{ background: "transparent", color: TH.textMute, border: `1px solid ${TH.border}` }}
                        data-testid={`contact-admin-mark-read-${q.query_id}`}>
                        <Eye size={12} weight="bold" /> Mark read
                      </button>
                    )}
                    {q.status !== "replied" && (
                      <button onClick={() => mark(q.query_id, "replied")}
                        disabled={!!busy[q.query_id]}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all disabled:opacity-50"
                        style={{ background: "transparent", color: TH.green, border: `1px solid ${TH.green}55` }}
                        data-testid={`contact-admin-mark-replied-${q.query_id}`}>
                        <CheckCircle size={12} weight="bold" /> Mark replied
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}
